import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useRole } from '@/hooks/useRole';
import type { MemberRole } from '@/types/database';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { UserPlus, Loader2, Copy, Share2, Trash2, Crown, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import {
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  ASSIGNABLE_ROLES,
  generateInviteToken,
  getInviteLink,
  copyInviteLink,
  shareInviteLink,
  shareInviteViaWhatsApp,
} from '@/lib/invites';

interface MemberRow {
  id: string;
  user_id: string;
  role: MemberRole;
  created_at: string | null;
  name: string;
  email: string;
  avatarUrl: string | null;
}

interface InviteRow {
  id: string;
  email: string;
  role: MemberRole;
  token: string;
  expires_at: string;
  created_at: string | null;
}

const ROLE_RANK: Record<MemberRole, number> = { owner: 0, admin: 1, member: 2, viewer: 3 };

function initials(name: string, email: string): string {
  const source = name?.trim() || email?.trim() || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function roleBadgeClass(role: MemberRole): string {
  switch (role) {
    case 'owner':
      return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30';
    case 'admin':
      return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30';
    case 'member':
      return 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function CompanyMembersPanel() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canManageTeam } = useRole();

  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);

  // per-row busy state (role change / removal)
  const [busyMemberId, setBusyMemberId] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<MemberRow | null>(null);
  const [removing, setRemoving] = useState(false);

  // invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('member');
  const [creating, setCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  const [revokingId, setRevokingId] = useState<string | null>(null);

  const companyId = activeCompany?.id ?? null;
  const companyName = activeCompany?.name ?? '';

  const load = useCallback(async () => {
    if (!companyId) {
      setMembers([]);
      setInvites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        supabase
          .from('company_members')
          .select('id, user_id, role, created_at')
          .eq('company_id', companyId),
        supabase
          .from('company_invites')
          .select('id, email, role, token, expires_at, created_at')
          .eq('company_id', companyId)
          .is('accepted_at', null)
          .order('created_at', { ascending: false }),
      ]);

      if (membersRes.error) throw membersRes.error;

      const rawMembers = membersRes.data ?? [];
      const userIds = rawMembers.map((m) => m.user_id);

      // `profiles` NÃO é mais legível por terceiros: a política aberta
      // (`Public can view profiles`) expunha stripe_*/pix_key/e-mail de todos os
      // usuários a qualquer um com a anon key e foi removida.
      //
      // A função devolve só nome/e-mail/avatar dos membros DESTA empresa, e só
      // para quem é membro dela. Allowlist de coluna porque política de RLS não
      // restringe coluna — liberar a tabela daria a um `viewer` acesso aos dados
      // de cobrança do dono.
      const profileMap = new Map<string, { full_name: string | null; email: string; logo_url: string | null }>();
      if (userIds.length > 0) {
        const { data: profiles } = await supabase.rpc('get_company_member_profiles', {
          p_company: companyId,
        });
        for (const p of (profiles as { id: string; full_name: string | null; email: string; logo_url: string | null }[] | null) ?? []) {
          profileMap.set(p.id, { full_name: p.full_name, email: p.email, logo_url: p.logo_url });
        }
      }

      const mapped: MemberRow[] = rawMembers
        .map((m) => {
          const p = profileMap.get(m.user_id);
          return {
            id: m.id,
            user_id: m.user_id,
            role: m.role as MemberRole,
            created_at: m.created_at,
            name: p?.full_name?.trim() || '',
            email: p?.email ?? '',
            avatarUrl: p?.logo_url ?? null,
          };
        })
        .sort((a, b) => {
          const r = ROLE_RANK[a.role] - ROLE_RANK[b.role];
          if (r !== 0) return r;
          return (a.name || a.email).localeCompare(b.name || b.email);
        });

      setMembers(mapped);

      // Pending invites: drop the ones already expired (defensive; server also
      // treats them as expired on accept).
      const now = Date.now();
      const pending = (invitesRes.data ?? []).filter(
        (i) => new Date(i.expires_at).getTime() > now,
      ) as InviteRow[];
      setInvites(pending);
    } catch (e) {
      console.error('[CompanyMembersPanel] load error', e);
      toast.error('Não foi possível carregar a equipe.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRoleChange = useCallback(
    async (member: MemberRow, nextRole: MemberRole) => {
      if (nextRole === member.role) return;
      setBusyMemberId(member.id);
      try {
        const { error } = await supabase
          .from('company_members')
          .update({ role: nextRole })
          .eq('id', member.id);
        if (error) throw error;
        setMembers((prev) =>
          prev.map((m) => (m.id === member.id ? { ...m, role: nextRole } : m)),
        );
        toast.success(`Cargo atualizado para ${ROLE_LABELS[nextRole]}.`);
      } catch (e) {
        console.error('[CompanyMembersPanel] role change error', e);
        toast.error('Não foi possível alterar o cargo.');
      } finally {
        setBusyMemberId(null);
      }
    },
    [],
  );

  const handleRemove = useCallback(async () => {
    if (!memberToRemove) return;
    setRemoving(true);
    try {
      const { error } = await supabase
        .from('company_members')
        .delete()
        .eq('id', memberToRemove.id);
      if (error) throw error;
      setMembers((prev) => prev.filter((m) => m.id !== memberToRemove.id));
      toast.success('Membro removido da equipe.');
      setMemberToRemove(null);
    } catch (e) {
      console.error('[CompanyMembersPanel] remove error', e);
      toast.error('Não foi possível remover o membro.');
    } finally {
      setRemoving(false);
    }
  }, [memberToRemove]);

  const resetInviteDialog = useCallback(() => {
    setInviteEmail('');
    setInviteRole('member');
    setCreatedToken(null);
    setCreating(false);
  }, []);

  const handleCreateInvite = useCallback(async () => {
    if (!companyId || !user) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      toast.warning('Digite um e-mail válido.');
      return;
    }
    // Guard against inviting someone already on the team.
    if (members.some((m) => m.email.toLowerCase() === email)) {
      toast.warning('Esse e-mail já faz parte da equipe.');
      return;
    }
    setCreating(true);
    const token = generateInviteToken();
    try {
      const { error } = await supabase.from('company_invites').insert({
        company_id: companyId,
        email,
        role: inviteRole,
        invited_by: user.id,
        token,
      });
      if (error) {
        // Unique partial index on (company_id, lower(email)) where accepted_at is null.
        if ((error as { code?: string }).code === '23505') {
          toast.warning('Já existe um convite pendente para este e-mail.');
        } else {
          throw error;
        }
        return;
      }
      setCreatedToken(token);
      await load();
      toast.success('Convite criado! Compartilhe o link.');
    } catch (e) {
      console.error('[CompanyMembersPanel] create invite error', e);
      toast.error('Não foi possível criar o convite.');
    } finally {
      setCreating(false);
    }
  }, [companyId, user, inviteEmail, inviteRole, members, load]);

  const handleRevokeInvite = useCallback(async (invite: InviteRow) => {
    setRevokingId(invite.id);
    try {
      const { error } = await supabase
        .from('company_invites')
        .delete()
        .eq('id', invite.id);
      if (error) throw error;
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      toast.success('Convite cancelado.');
    } catch (e) {
      console.error('[CompanyMembersPanel] revoke invite error', e);
      toast.error('Não foi possível cancelar o convite.');
    } finally {
      setRevokingId(null);
    }
  }, []);

  const memberCount = members.length;

  const canManage = canManageTeam;

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <CardContent className="flex items-center gap-3 p-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-44" />
              </div>
              <Skeleton className="h-6 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + invite CTA */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>
            {memberCount} {memberCount === 1 ? 'membro' : 'membros'}
          </span>
        </div>
        {canManage && (
          <Button3D
            size="sm"
            onClick={() => {
              resetInviteDialog();
              setInviteOpen(true);
            }}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Convidar
          </Button3D>
        )}
      </div>

      {/* Members list */}
      <div className="space-y-3">
        {members.map((member) => {
          const isSelf = member.user_id === user?.id;
          const isOwnerRow = member.role === 'owner';
          // Owner row and your own row are locked. Everyone else is manageable
          // by an owner/admin (RLS is the real gate).
          const canEditRow = canManage && !isOwnerRow && !isSelf;
          return (
            <Card key={member.id}>
              <CardContent className="flex items-center gap-3 p-4">
                <Avatar className="h-10 w-10">
                  {member.avatarUrl && <AvatarImage src={member.avatarUrl} alt={member.name || member.email} />}
                  <AvatarFallback>{initials(member.name, member.email)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium">
                      {member.name || member.email || 'Sem nome'}
                    </span>
                    {isOwnerRow && <Crown className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                    {isSelf && <span className="text-xs text-muted-foreground">(você)</span>}
                  </div>
                  {member.email && (
                    <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                  )}
                </div>

                {canEditRow ? (
                  <div className="flex items-center gap-2">
                    <Select
                      value={member.role}
                      onValueChange={(v) => handleRoleChange(member, v as MemberRole)}
                      disabled={busyMemberId === member.id}
                    >
                      <SelectTrigger className="h-8 w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNABLE_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setMemberToRemove(member)}
                      disabled={busyMemberId === member.id}
                      aria-label="Remover membro"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Badge variant="outline" className={roleBadgeClass(member.role)}>
                    {ROLE_LABELS[member.role]}
                  </Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pending invites */}
      {(canManage && invites.length > 0) && (
        <div className="space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Clock className="h-4 w-4" />
            Convites pendentes
          </h3>
          {invites.map((invite) => (
            <Card key={invite.id} className="border-dashed">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{invite.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {ROLE_LABELS[invite.role]} · aguardando aceite
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[#25D366]"
                    onClick={() => shareInviteViaWhatsApp(invite.token, companyName)}
                    aria-label="Enviar por WhatsApp"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => copyInviteLink(invite.token)}
                    aria-label="Copiar link"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleRevokeInvite(invite)}
                    disabled={revokingId === invite.id}
                    aria-label="Cancelar convite"
                  >
                    {revokingId === invite.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!canManage && (
        <p className="text-xs text-muted-foreground">
          Somente o dono e administradores podem convidar ou gerenciar membros.
        </p>
      )}

      {/* Remove member confirmation */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(o) => !o && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover da equipe?</AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove?.name || memberToRemove?.email} perderá o acesso aos dados de{' '}
              {companyName}. Os orçamentos, clientes e itens criados permanecem na empresa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRemove();
              }}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite dialog */}
      <Dialog
        open={inviteOpen}
        onOpenChange={(o) => {
          setInviteOpen(o);
          if (!o) resetInviteDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convidar para a equipe</DialogTitle>
            <DialogDescription>
              {createdToken
                ? 'Convite criado. Envie o link para a pessoa entrar na equipe.'
                : `Envie um convite para colaborar em ${companyName}.`}
            </DialogDescription>
          </DialogHeader>

          {!createdToken ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="invite-email">E-mail</Label>
                <Input
                  id="invite-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  placeholder="pessoa@exemplo.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Cargo</Label>
                <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as MemberRole)}>
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[inviteRole]}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-md border bg-muted/40 p-3">
                <p className="break-all text-xs text-muted-foreground">{getInviteLink(createdToken)}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button3D
                  color="whatsapp"
                  onClick={() => shareInviteViaWhatsApp(createdToken, companyName)}
                >
                  <WhatsAppIcon className="mr-2 h-4 w-4" />
                  WhatsApp
                </Button3D>
                <Button3D color="gray" onClick={() => copyInviteLink(createdToken)}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar
                </Button3D>
              </div>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => shareInviteLink(createdToken, companyName)}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Mais opções de envio
              </Button>
            </div>
          )}

          <DialogFooter>
            {!createdToken ? (
              <Button3D onClick={handleCreateInvite} disabled={creating}>
                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Criar convite
              </Button3D>
            ) : (
              <Button3D
                onClick={() => {
                  setInviteOpen(false);
                  resetInviteDialog();
                }}
              >
                Concluir
              </Button3D>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
